import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { FormattedNumber, FormattedMessage } from 'react-intl';

import { VODCSSModules } from 'shared/utils/wix-connect';
import { canShowMainItemTitle, isCompactMainActionsOnlyIcons, getMainItemOverlayOpacity } from 'shared/selectors/app-settings';
import { getVideoCoverUrl } from 'shared/ui-selectors/video/cover';
import { getChannelCoverUrl } from 'shared/ui-selectors/channel/cover';

import Title from 'widget/components/player-overlay/title/title';
import ResizedImage from 'shared/components/resized-image/resized-image';

import getPlayButtonConfigForVideo from './ui-selectors/video-overlay/play/button-config';
import getPlayButtonConfigForChannel from './ui-selectors/channel-overlay/play/button-config';
import getPaidAccessButtonConfigForVideo from './ui-selectors/video-overlay/paid-access/button-config';
import getPaidAccessButtonConfigForChannel from './ui-selectors/channel-overlay/paid-access/button-config';

import PlayIcon from 'static/images/play.svg';
import PaidIcon from 'static/images/paid.svg';

import styles from './overlay.styl';


export class OverlayBase extends React.Component {

  static propTypes = {
    width: React.PropTypes.number,
    height: React.PropTypes.number,

    className: React.PropTypes.string,
    channelData: React.PropTypes.object,
    videoItem: React.PropTypes.object,

    mainItemOverlayOpacity: React.PropTypes.number,
    canShowMainItemTitle: React.PropTypes.bool,
    isMainActionsOnlyIcons: React.PropTypes.bool,

    playButtonConfig: React.PropTypes.object,
    paidAccessButtonConfig: React.PropTypes.object,

    onPlayRequest: React.PropTypes.func,
    onPlayMemberOnlyRequest: React.PropTypes.func,
    onPlayWithoutPreviewRequest: React.PropTypes.func,
    onSubscriptionRequest: React.PropTypes.func,
    onPurchaseRequest: React.PropTypes.func,
  };

  static defaultProps = {
    showChannelCover: false,
    onPlayRequest: _.noop,
    onPlayMemberOnlyRequest: _.noop,
    onPlayWithoutPreviewRequest: _.noop,
    onPurchaseRequest: _.noop,
    onSubscriptionRequest: _.noop,
  };

  get opacityStyles() {
    const { mainItemOverlayOpacity } = this.props;
    const opacity = _.round(mainItemOverlayOpacity / 100, 2);

    return {
      backgroundColor: `rgba(0,0,0,${opacity})`,
    };
  }

  get clampOptions() {
    return { lineClamp: 2 };
  }

  // Is differs between channel and video and realized at parent class
  get titleText() {
    return null;
  }

  // Is differs between channel and video and realized at parent class
  get coverUrl() {
    return null;
  }

  get title() {
    if (!this.props.canShowMainItemTitle) {
      return null;
    }

    const titleText = this.titleText;

    if (!titleText) {
      return null;
    }

    return (
      <Title styleName="title"
             text={titleText}
             clampOptions={this.clampOptions} />
    );
  }

  @autobind
  handlePlayButtonClick(event) {
    event.stopPropagation();
    const { playButtonConfig } = this.props;
    playButtonConfig.callback();
  }

  renderPlayButton() {
    const { playButtonConfig, isMainActionsOnlyIcons } = this.props;

    if (!playButtonConfig) {
      return null;
    }

    const { translationData } = playButtonConfig;

    const styleNames = classnames('button', 'play-button', {
      'only-icon': isMainActionsOnlyIcons,
    });

    return (
      <a href="#"
         styleName={styleNames}
         onClick={this.handlePlayButtonClick}
         className="qa-widget-overlay-play-button"
         data-hook="playButton">
        <PlayIcon styleName="icon" />
        <span styleName="text">
          <FormattedMessage {...translationData.props} />
        </span>
      </a>
    );
  }

  @autobind
  handlePaidAccessButtonClick(event) {
    event.stopPropagation();
    const { paidAccessButtonConfig } = this.props;
    paidAccessButtonConfig.callback();
  }

  renderPaidAccessButton() {
    const { isMainActionsOnlyIcons, paidAccessButtonConfig } = this.props;

    if (!paidAccessButtonConfig) {
      return null;
    }

    const { translationData } = paidAccessButtonConfig;
    const { priceModel: { price, currency } } = translationData;

    const values = {
      price: <FormattedNumber value={price}
                              style="currency"
                              currency={currency} />,
    };

    const styleNames = classnames('button', 'paid-access-button', {
      'only-icon': isMainActionsOnlyIcons,
    });

    return (
      <a href="#"
         styleName={styleNames}
         onClick={this.handlePaidAccessButtonClick}
         className="qa-widget-overlay-buy-from-button"
         data-hook="paidAccessButton">
        <PaidIcon styleName="icon" />
        <span styleName="text">
          <FormattedMessage {...translationData.props}
                              values={values} />
        </span>
      </a>
    );
  }

  get buttons() {
    const play = this.renderPlayButton();
    const paidAccess = this.renderPaidAccessButton();

    return (
      <div styleName="buttons-wrapper">
        {play}
        {paidAccess}
      </div>
    );
  }

  get cover() {
    const { width, height } = this.props;

    return (
      <ResizedImage src={this.coverUrl}
                    styleName="cover"
                    width={width}
                    height={height} />
    );
  }

  render() {
    const {
      channelData,
      videoItem,
      className,
    } = this.props;

    if (!channelData || !videoItem) {
      return null;
    }

    return (
      <div styleName="container"
           className={className}>
        {this.cover}
        <div style={this.opacityStyles}
             styleName="content">
          <div styleName="top-content"
               dir="ltr">
            <div styleName="title-block">
              {this.title}
            </div>
            {this.buttons}
          </div>
        </div>
      </div>
    );
  }
}

@connect((state, ownProps) => ({
  playButtonConfig: getPlayButtonConfigForVideo(ownProps),
  paidAccessButtonConfig: getPaidAccessButtonConfigForVideo(ownProps),
  mainItemOverlayOpacity: getMainItemOverlayOpacity(state),
  canShowMainItemTitle: canShowMainItemTitle(state),
  isMainActionsOnlyIcons: isCompactMainActionsOnlyIcons(state),
}))
@VODCSSModules(styles)
export class VideoOverlay extends OverlayBase {
  static displayName = 'VideoOverlay';

  get titleText() {
    const { videoItem } = this.props;

    return videoItem.title;
  }

  get coverUrl() {
    const { videoItem } = this.props;

    return getVideoCoverUrl(videoItem);
  }
}

@connect((state, ownProps) => ({
  playButtonConfig: getPlayButtonConfigForChannel(ownProps),
  paidAccessButtonConfig: getPaidAccessButtonConfigForChannel(ownProps),
  mainItemOverlayOpacity: getMainItemOverlayOpacity(state),
  canShowMainItemTitle: canShowMainItemTitle(state),
  isMainActionsOnlyIcons: isCompactMainActionsOnlyIcons(state),
}))
@VODCSSModules(styles)
export class ChannelOverlay extends OverlayBase {
  static displayName = 'ChannelOverlay';

  get titleText() {
    const { channelData } = this.props;

    return channelData.title;
  }

  get coverUrl() {
    const { channelData, videoItem } = this.props;

    return getChannelCoverUrl(channelData) || getVideoCoverUrl(videoItem);
  }
}




// WEBPACK FOOTER //
// ./src/widget/views/desktop/compact/components/player-overlay/overlay.js